import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faXmark, faArrowLeft, faArrowRight, faCheck } from '@fortawesome/pro-duotone-svg-icons';
import { ButtonModule } from 'primeng/button';

import { TourService } from '../../services/tour.service';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

type Placement = 'bottom' | 'top' | 'right' | 'left';

interface PopoverPosition {
  top: number;
  left: number;
  placement: Placement;
}

const CUTOUT_PADDING = 6;
const POPOVER_GAP = 14;
const POPOVER_WIDTH = 320;
const POPOVER_HEIGHT_EST = 200;
const VIEWPORT_MARGIN = 12;

/**
 * Global step-tour overlay. Mounted once in app.component.html.
 * Renders nothing when no tour is active. Non-blocking: the page stays fully interactive.
 *
 * Layers (z-index):
 *   9991  cutout   (soft accent halo around the anchor; pointer-events: none)
 *   9992  popover  (dark UI, anchored relative to the cutout; the only interactive piece)
 */
@Component({
  selector: 'app-tour-overlay',
  standalone: true,
  imports: [CommonModule, TranslateModule, FontAwesomeModule, ButtonModule],
  templateUrl: './tour-overlay.component.html',
  styleUrl: './tour-overlay.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TourOverlayComponent implements AfterViewInit, OnDestroy {
  protected readonly tour = inject(TourService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('popoverRef') protected popoverRef?: ElementRef<HTMLDivElement>;

  protected readonly targetRect = signal<Rect | null>(null);
  protected readonly popoverPos = signal<PopoverPosition>({ top: 0, left: 0, placement: 'bottom' });

  protected readonly faXmark = faXmark;
  protected readonly faArrowLeft = faArrowLeft;
  protected readonly faArrowRight = faArrowRight;
  protected readonly faCheck = faCheck;

  protected readonly cutoutStyle = computed(() => {
    const r = this.targetRect();
    if (!r) return null;
    return {
      top: `${r.top - CUTOUT_PADDING}px`,
      left: `${r.left - CUTOUT_PADDING}px`,
      width: `${r.width + CUTOUT_PADDING * 2}px`,
      height: `${r.height + CUTOUT_PADDING * 2}px`,
    };
  });

  private rafId: number | null = null;
  private resizeObs?: ResizeObserver;
  private mutationObs?: MutationObserver;
  private observedAnchors: HTMLElement[] = [];

  constructor() {
    effect(() => {
      const anchors = this.tour.currentAnchors();
      this.swapObservedAnchors(anchors);
      if (anchors.length > 0) {
        this.scheduleUpdate();
      } else {
        this.targetRect.set(null);
      }
    });

    effect(() => {
      const r = this.targetRect();
      if (r) {
        queueMicrotask(() => this.computePopoverPosition(r));
      }
    });
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.onWindowChange, { passive: true });
    window.addEventListener('scroll', this.onWindowChange, { capture: true, passive: true });
    window.addEventListener('keydown', this.onKeyDown);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('resize', this.onWindowChange);
      window.removeEventListener('scroll', this.onWindowChange, { capture: true } as EventListenerOptions);
      window.removeEventListener('keydown', this.onKeyDown);
    });
  }

  ngOnDestroy(): void {
    this.disconnectObservers();
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
  }

  protected onSkip(): void {
    this.tour.end();
  }

  protected onPrev(): void {
    this.tour.prev();
  }

  protected onNext(): void {
    this.tour.next();
  }

  private readonly onWindowChange = (): void => {
    if (this.tour.activeTour()) this.scheduleUpdate();
  };

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (!this.tour.activeTour()) return;
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
      return;
    }
    switch (e.key) {
      case 'ArrowRight':
      case ' ':
      case 'Enter':
        e.preventDefault();
        this.tour.next();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.tour.prev();
        break;
      case 'Escape':
        e.preventDefault();
        this.tour.end();
        break;
    }
  };

  private swapObservedAnchors(next: HTMLElement[]): void {
    if (next.length === this.observedAnchors.length && next.every((el, i) => el === this.observedAnchors[i])) {
      return;
    }
    this.disconnectObservers();
    this.observedAnchors = next;
    if (next.length > 0) {
      this.resizeObs = new ResizeObserver(() => this.scheduleUpdate());
      next.forEach(el => this.resizeObs!.observe(el));
      this.mutationObs = new MutationObserver(() => this.scheduleUpdate());
      this.mutationObs.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class', 'style'] });
    }
  }

  private disconnectObservers(): void {
    this.resizeObs?.disconnect();
    this.resizeObs = undefined;
    this.mutationObs?.disconnect();
    this.mutationObs = undefined;
  }

  private scheduleUpdate(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      const anchors = this.tour.currentAnchors();
      if (anchors.length === 0) {
        this.targetRect.set(null);
        return;
      }
      const rects = anchors.map(a => a.getBoundingClientRect());
      const top = Math.min(...rects.map(r => r.top));
      const left = Math.min(...rects.map(r => r.left));
      const bottom = Math.max(...rects.map(r => r.bottom));
      const right = Math.max(...rects.map(r => r.right));
      this.targetRect.set({ top, left, width: right - left, height: bottom - top });
    });
  }

  /**
   * Picks a placement based on where the anchor lives in the viewport, then validates
   * it fits. Position-driven (left → right, top → bottom, etc.) catches the natural
   * fit for sidebar items (right) and topbar items (bottom) without falling back to
   * the visually-awkward "right" choice for topbar elements just because there's room.
   */
  private computePopoverPosition(rect: Rect): void {
    const popHeight = this.popoverRef?.nativeElement.offsetHeight ?? POPOVER_HEIGHT_EST;
    const popWidth = this.popoverRef?.nativeElement.offsetWidth ?? POPOVER_WIDTH;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const fits = {
      right:  rect.left + rect.width + POPOVER_GAP + popWidth + VIEWPORT_MARGIN <= vw,
      left:   rect.left - POPOVER_GAP - popWidth - VIEWPORT_MARGIN >= 0,
      bottom: rect.top + rect.height + POPOVER_GAP + popHeight + VIEWPORT_MARGIN <= vh,
      top:    rect.top - POPOVER_GAP - popHeight - VIEWPORT_MARGIN >= 0,
    };

    // Position-driven preference order
    const targetCenterX = rect.left + rect.width / 2;
    const targetCenterY = rect.top + rect.height / 2;
    const inLeftThird = targetCenterX < vw / 3;
    const inRightThird = targetCenterX > (vw * 2) / 3;
    const inTopThird = targetCenterY < vh / 3;
    const inBottomThird = targetCenterY > (vh * 2) / 3;

    let order: Placement[];
    if (inLeftThird) {
      order = ['right', 'bottom', 'top', 'left'];          // sidebar
    } else if (inRightThird) {
      order = ['left', 'bottom', 'top', 'right'];          // right-edge chrome
    } else if (inTopThird) {
      order = ['bottom', 'right', 'left', 'top'];          // topbar — the search-trigger case
    } else if (inBottomThird) {
      order = ['top', 'right', 'left', 'bottom'];          // bottom dock / status bar
    } else {
      order = ['bottom', 'right', 'top', 'left'];          // center — default
    }

    let placement: Placement = order.find(p => fits[p]) ?? 'bottom';
    let top = 0;
    let left = 0;

    switch (placement) {
      case 'right':
        left = rect.left + rect.width + POPOVER_GAP;
        top = rect.top + rect.height / 2 - popHeight / 2;
        break;
      case 'left':
        left = rect.left - POPOVER_GAP - popWidth;
        top = rect.top + rect.height / 2 - popHeight / 2;
        break;
      case 'bottom':
        top = rect.top + rect.height + POPOVER_GAP;
        left = rect.left + rect.width / 2 - popWidth / 2;
        break;
      case 'top':
        top = rect.top - POPOVER_GAP - popHeight;
        left = rect.left + rect.width / 2 - popWidth / 2;
        break;
    }

    // Clamp inside viewport with margin.
    top = Math.max(VIEWPORT_MARGIN, Math.min(vh - popHeight - VIEWPORT_MARGIN, top));
    left = Math.max(VIEWPORT_MARGIN, Math.min(vw - popWidth - VIEWPORT_MARGIN, left));

    this.popoverPos.set({ top, left, placement });
  }
}
